import {Button, Checkbox, TextInput} from "omoplay";
import {useEffect, useState} from "react";
import InputGroup from "../InputGroup";
import {getCompleteRedirect, postCompleteRedirectSave} from "../../../api/project/project.api";
import {X} from "lucide-react";
import {
    BUTTON_NAME_MAX_LENGTH,
    buildExampleLink,
    DEFAULT_SETTINGS,
    MAX_ALLOWED_HOSTS,
    MAX_DELAY_SEC,
    MIN_DELAY_SEC,
    normalizeHost,
    validateSettings,
} from "../../../libs/completeRedirect";

/*
  참여 완료 후 이동(2026-10-27, 구조 개편 2026-10-29) — 프로젝트 상세 > 개요 탭 카드.

  고객이 이벤트 참여(회원가입)를 마친 결과 화면에서 고객사 사이트(자사몰 예약·주문 등)로 보내는 설정.
  섹션 순서(사용자 결정):
    1. 허용 도메인(필수) — 등록한 도메인이 아니면 디폴트 URL 이든 파라미터든 무시(큐싱 방지). 서버가 최종 판정
    2. 기본 설정 — 버튼명(필수)·디폴트 이동 URL(필수)
    3. 진입 URL 로 이동 주소 받기(?redirect_url=…) — 매장·배너마다 다른 주소. 파라미터가 디폴트 URL 보다 우선
    4. n초 후 자동 이동 — 체크하면 대기 시간(2~60초)과 안내
    5. 쿠폰 화면에도 버튼 표시 — 별도 버튼명(비우면 기본 버튼명). 이 화면은 자동 이동하지 않는다
  검증·우선순위의 원본은 백엔드 `libs/CompleteRedirectPolicy.kt`, 프론트 1차 거울은 `libs/completeRedirect.js`.

  레이아웃: 입력칸 + 버튼 한 줄은 `flex` 에서 입력 래퍼 `flex:1; minWidth:0`, 버튼 `flexShrink:0` —
  종전엔 입력칸이 100% 라 옆 버튼이 찌그러졌다(2026-10-29 테스트 피드백).
*/
const fromServer = (data) => ({
    ...DEFAULT_SETTINGS,
    ...(data || {}),
    buttonName: data?.buttonName ?? "",
    url: data?.url ?? "",
    allowedHosts: data?.allowedHosts ?? [],
    benefitButtonName: data?.benefitButtonName ?? "",
});

const SectionTitle = ({children, required}) => (
    <div className="text-sm font-semibold text-gray-900">
        {children}{required && <span className="text-red-500"> (필수)</span>}
    </div>
);

const Help = ({children}) => <div className="text-gray-500 text-sm">{children}</div>;

/** 입력칸 + 오른쪽 버튼 한 줄. 입력은 남는 폭을 쓰고 버튼은 줄지 않는다. */
const InputWithButton = ({input, button}) => (
    <div className="flex gap-2 items-start">
        <div style={{flex: 1, minWidth: 0}}>{input}</div>
        <div style={{flexShrink: 0}}>{button}</div>
    </div>
);

const ProjectDetailCompleteRedirect = ({projectId, entryUrl}) => {
    const [settings, setSettings] = useState({...DEFAULT_SETTINGS});
    const [hostInput, setHostInput] = useState("");
    const [copied, setCopied] = useState(false);

    useEffect(() => {
        getCompleteRedirect(projectId).then(({data}) => {
            setSettings(fromServer(data));
        });
    }, [projectId]);

    const update = (patch) => setSettings((prev) => ({...prev, ...patch}));

    const addHost = () => {
        const host = normalizeHost(hostInput);
        if (!host) {
            alert("도메인 형식이 올바르지 않습니다. 예: www.example.com (주소를 통째로 붙여넣어도 도메인만 저장됩니다)");
            return;
        }
        if (settings.allowedHosts.length >= MAX_ALLOWED_HOSTS) {
            alert(`허용 도메인은 최대 ${MAX_ALLOWED_HOSTS}개까지 등록할 수 있습니다.`);
            return;
        }
        if (!settings.allowedHosts.includes(host)) update({allowedHosts: [...settings.allowedHosts, host]});
        setHostInput("");
    };

    const onSubmit = () => {
        const result = validateSettings(settings);
        if (!result.ok) {
            alert(result.message);
            return;
        }
        postCompleteRedirectSave(projectId, result.settings).then(() => {
            setSettings(fromServer(result.settings));
            alert("저장 되었습니다.");
        });
    };

    const exampleTarget = settings.url || (settings.allowedHosts[0] ? `https://${settings.allowedHosts[0]}/page?store_id=1234` : "");
    const exampleLink = buildExampleLink(entryUrl, exampleTarget);

    const copyExample = () => {
        navigator.clipboard?.writeText(exampleLink).then(() => {
            setCopied(true);
            setTimeout(() => setCopied(false), 1500);
        }).catch(() => alert("복사하지 못했습니다. 직접 선택해 복사해 주세요."));
    };

    return (
        <InputGroup label={
            <div className="flex-align-center justify-between">
                <div className="text-[16px]">참여 완료 후 이동</div>
                <Button variants="dark" value="적용하기" onClick={onSubmit} />
            </div>
        } description="이벤트 참여(회원가입)를 마친 고객을 고객사 사이트(예약·주문 페이지 등)로 보냅니다. 결과 화면과 쿠폰 화면에 표시됩니다.">
            <div className="flex-col gap-6">
                <Checkbox checked={settings.used} onChecked={(flag) => update({used: flag})}
                          item={{label: "참여 완료 후 고객사 사이트로 이동 기능을 사용합니다.", value: "used"}} />

                {settings.used && (
                    <>
                        {/* 1. 허용 도메인 */}
                        <div className="flex-col gap-2">
                            <SectionTitle required>허용 도메인</SectionTitle>
                            <Help>
                                등록한 도메인으로만 이동합니다. 등록되지 않은 주소는 보안(악성 링크 방지)을 위해 조용히 무시됩니다.
                                www 포함 여부까지 정확히 입력하세요. 서브도메인을 여러 개 쓰면 모두 등록합니다.
                            </Help>
                            <InputWithButton
                                input={<TextInput value={hostInput} placeholder="www.example.com"
                                                  onChange={(e) => setHostInput(e.target.value)}
                                                  onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addHost(); } }} />}
                                button={<Button variants="outlined" value="+ 추가" onClick={addHost} />}
                            />
                            {settings.allowedHosts.length === 0 ? (
                                <div className="text-sm text-red-500">등록된 도메인이 없습니다. 도메인이 없으면 저장할 수 없습니다.</div>
                            ) : (
                                <div className="flex-col gap-1 border rounded" style={{padding: "8px 12px"}}>
                                    {settings.allowedHosts.map((host) => (
                                        <div key={host} className="flex-align-center justify-between text-sm" style={{padding: "4px 0"}}>
                                            <code>{host}</code>
                                            <button type="button" className="text-gray-400 hover:text-gray-700" aria-label={`${host} 삭제`}
                                                    onClick={() => update({allowedHosts: settings.allowedHosts.filter((h) => h !== host)})}>
                                                <X size={16} />
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>

                        {/* 2. 기본 설정 */}
                        <div className="flex-col gap-3">
                            <SectionTitle required>기본 설정</SectionTitle>
                            <Help>결과 화면(당첨 · 응모 완료 · 이미 참여 · 중복 참여)에 표시되는 이동 버튼입니다. 아래 설정이 없거나 거부될 때 항상 이 값으로 동작합니다.</Help>
                            <div className="flex-col gap-1">
                                <div className="text-sm text-gray-700">버튼명 ({BUTTON_NAME_MAX_LENGTH}자)</div>
                                <div style={{maxWidth: "320px"}}>
                                    <TextInput value={settings.buttonName} placeholder="예: 매장 예약하러 가기" maxLength={BUTTON_NAME_MAX_LENGTH}
                                               onChange={(e) => update({buttonName: e.target.value})} />
                                </div>
                            </div>
                            <div className="flex-col gap-1">
                                <div className="text-sm text-gray-700">디폴트 이동 URL</div>
                                <InputWithButton
                                    input={<TextInput value={settings.url} placeholder="https://www.example.com/reserve"
                                                      onChange={(e) => update({url: e.target.value})} />}
                                    button={<Button variants="default" value="URL 확인" disabled={!settings.url}
                                                    onClick={() => window.open(settings.url)} />}
                                />
                                <Help>https:// 로 시작하는 전체 주소. 허용 도메인에 등록된 도메인이어야 합니다.</Help>
                            </div>
                        </div>

                        {/* 3. 진입 URL 파라미터 */}
                        <div className="flex-col gap-2">
                            <Checkbox checked={settings.allowQueryParam} onChecked={(flag) => update({allowQueryParam: flag})}
                                      item={{label: "진입 URL 로 이동 주소 받기 (?redirect_url=…) — 매장·배너마다 다른 주소로 보낼 때", value: "allowQueryParam"}} />
                            {settings.allowQueryParam && (
                                <div className="flex-col gap-2 bg-grey-0 rounded text-sm" style={{padding: "12px"}}>
                                    <div>
                                        진입 주소 뒤에 <code>?redirect_url=</code> 와 <b>인코딩한 이동 주소</b>를 붙여 배포합니다.
                                        이 값이 디폴트 이동 URL 보다 우선하며, 허용 도메인 검사는 똑같이 적용됩니다.
                                    </div>
                                    <div className="font-semibold">예시 링크</div>
                                    <InputWithButton
                                        input={<code className="block bg-white border rounded px-2 py-1 break-all">{exampleLink}</code>}
                                        button={<Button variants="outlined" value={copied ? "복사됨" : "복사"} onClick={copyExample} />}
                                    />
                                    <ul className="list-disc pl-5 text-gray-600">
                                        <li>이동 주소 전체를 <b>encodeURIComponent</b>(PHP: rawurlencode)로 <b>한 번만</b> 인코딩합니다. 인코딩하지 않으면 주소 안의 <code>&amp;</code> 뒤가 잘립니다.</li>
                                        <li>https:// 로 시작하는 전체 주소, 1,000자 이내. <code>#</code> 뒤(프래그먼트)는 전달되지 않습니다.</li>
                                        <li>고객은 자사몰에 로그인하지 않은 상태로 도착합니다. 이동 페이지가 비로그인 상태를 처리해야 합니다.</li>
                                        <li>알림톡·친구톡의 재진입 링크에는 이 파라미터가 붙지 않습니다(디폴트 이동 URL 로 동작).</li>
                                        <li>오프라인 QR 은 주소가 길어져 QR 이 촘촘해집니다. QR 은 디폴트 이동 URL 을 권장합니다.</li>
                                    </ul>
                                </div>
                            )}
                        </div>

                        {/* 4. 자동 이동 */}
                        <div className="flex-col gap-2">
                            <Checkbox checked={settings.autoRedirect} onChecked={(flag) => update({autoRedirect: flag})}
                                      item={{label: "n초 후 자동 이동 — 결과 화면이 뜬 뒤 자동으로 이동합니다", value: "autoRedirect"}} />
                            {settings.autoRedirect && (
                                <div className="flex-col gap-2 bg-grey-0 rounded" style={{padding: "12px"}}>
                                    <div className="flex-align-center gap-2 text-sm">
                                        <span>결과 화면 표시 후</span>
                                        <div style={{width: "80px", flexShrink: 0}}>
                                            <TextInput type="number" min={MIN_DELAY_SEC} max={MAX_DELAY_SEC} value={settings.delaySec}
                                                       onChange={(e) => update({delaySec: e.target.value})} />
                                        </div>
                                        <span>초 뒤 자동으로 이동 ({MIN_DELAY_SEC}~{MAX_DELAY_SEC}초, 기본 3초)</span>
                                    </div>
                                    <Help>
                                        대기 시간이 짧으면 고객이 당첨 내용을 확인하기 전에 넘어갈 수 있습니다. {MIN_DELAY_SEC}초 미만은 설정할 수 없습니다.
                                        결과 화면에는 남은 시간 옆에 <b>'이 화면에 머무르기'</b> 버튼이 함께 표시되고, '카카오톡으로도 당첨 내용을 전송해 드렸어요' 안내도 그대로 보입니다.
                                        쿠폰(당첨 정보 확인) 화면에서는 자동 이동하지 않습니다.
                                    </Help>
                                </div>
                            )}
                        </div>

                        {/* 5. 쿠폰 화면 */}
                        <div className="flex-col gap-2">
                            <Checkbox checked={settings.showOnBenefitPage} onChecked={(flag) => update({showOnBenefitPage: flag})}
                                      item={{label: "쿠폰 화면(당첨 정보 확인)에도 버튼 표시 — 이 화면은 자동 이동하지 않습니다.", value: "showOnBenefitPage"}} />
                            {settings.showOnBenefitPage && (
                                <div className="flex-col gap-1 bg-grey-0 rounded" style={{padding: "12px"}}>
                                    <div className="text-sm text-gray-700">쿠폰 화면 버튼명 ({BUTTON_NAME_MAX_LENGTH}자, 비우면 기본 버튼명 "{settings.buttonName || "…"}" 사용)</div>
                                    <div style={{maxWidth: "320px"}}>
                                        <TextInput value={settings.benefitButtonName} placeholder="예: 쿠폰 쓰러 가기" maxLength={BUTTON_NAME_MAX_LENGTH}
                                                   onChange={(e) => update({benefitButtonName: e.target.value})} />
                                    </div>
                                    <Help>쿠폰 화면은 '당첨 정보 확인' 버튼이나 혜택 메시지 링크로 들어오는 화면입니다. 이동 주소는 위와 같은 규칙으로 정해집니다.</Help>
                                </div>
                            )}
                        </div>
                    </>
                )}
            </div>
        </InputGroup>
    );
};

export default ProjectDetailCompleteRedirect;
